export type TaxIdentifierFormat = {
  /** Type attendu par Stripe pour ce pays (`tax_ids.create`). */
  stripeType: string;
  pattern: RegExp;
  example: string;
  /** Format canonique sans séparateur : espaces, points et tirets sont retirés avant envoi. */
  compact: boolean;
};

const euVat = (pattern: RegExp, example: string): TaxIdentifierFormat => ({
  stripeType: 'eu_vat',
  pattern,
  example,
  compact: true,
});

/**
 * Un pays absent de cette table n'est pas pris en charge : le back ne saurait pas quel type
 * envoyer à Stripe, l'appel échouerait et laisserait un client orphelin. Le formulaire
 * désactive donc le champ. Ajouter une entrée suffit à ouvrir un nouveau pays.
 */
export const TAX_IDENTIFIER_FORMATS: Readonly<Record<string, TaxIdentifierFormat>> = {
  // Union européenne
  AT: euVat(/^ATU\d{8}$/, 'ATU12345678'),
  BE: euVat(/^BE[01]\d{9}$/, 'BE0123456789'),
  BG: euVat(/^BG\d{9,10}$/, 'BG123456789'),
  CY: euVat(/^CY\d{8}[A-Z]$/, 'CY12345678Z'),
  CZ: euVat(/^CZ\d{8,10}$/, 'CZ12345678'),
  DE: euVat(/^DE\d{9}$/, 'DE123456789'),
  DK: euVat(/^DK\d{8}$/, 'DK12345678'),
  EE: euVat(/^EE\d{9}$/, 'EE123456789'),
  ES: euVat(/^ES[A-Z0-9]\d{7}[A-Z0-9]$/, 'ESA1234567Z'),
  FI: euVat(/^FI\d{8}$/, 'FI12345678'),
  FR: euVat(/^FR[A-Z0-9]{2}\d{9}$/, 'FR12345678901'),
  // La Grèce utilise le préfixe EL et non son code ISO GR
  GR: euVat(/^EL\d{9}$/, 'EL123456789'),
  HR: euVat(/^HR\d{11}$/, 'HR12345678901'),
  HU: euVat(/^HU\d{8}$/, 'HU12345678'),
  IE: euVat(/^IE(\d{7}[A-Z]{1,2}|\d[A-Z0-9+*]\d{5}[A-Z])$/, 'IE1234567AB'),
  IT: euVat(/^IT\d{11}$/, 'IT12345678901'),
  LT: euVat(/^LT(\d{9}|\d{12})$/, 'LT123456789'),
  LU: euVat(/^LU\d{8}$/, 'LU12345678'),
  LV: euVat(/^LV\d{11}$/, 'LV12345678901'),
  MT: euVat(/^MT\d{8}$/, 'MT12345678'),
  NL: euVat(/^NL\d{9}B\d{2}$/, 'NL123456789B01'),
  PL: euVat(/^PL\d{10}$/, 'PL1234567890'),
  PT: euVat(/^PT\d{9}$/, 'PT123456789'),
  RO: euVat(/^RO\d{2,10}$/, 'RO1234567890'),
  SE: euVat(/^SE\d{12}$/, 'SE123456789012'),
  SI: euVat(/^SI\d{8}$/, 'SI12345678'),
  SK: euVat(/^SK\d{10}$/, 'SK1234567890'),

  // Hors Union européenne
  AE: { stripeType: 'ae_trn', pattern: /^\d{15}$/, example: '123456789012345', compact: true },
  AU: { stripeType: 'au_abn', pattern: /^\d{11}$/, example: '12345678912', compact: true },
  CA: { stripeType: 'ca_bn', pattern: /^\d{9}(RT\d{4})?$/, example: '123456789', compact: true },
  CH: {
    stripeType: 'ch_vat',
    pattern: /^CHE-\d{3}\.\d{3}\.\d{3}( (MWST|TVA|IVA))?$/,
    example: 'CHE-123.456.789 TVA',
    compact: false,
  },
  GB: {
    stripeType: 'gb_vat',
    pattern: /^GB(\d{9}|\d{12}|(GD|HA)\d{3})$/,
    example: 'GB123456789',
    compact: true,
  },
  IL: { stripeType: 'il_vat', pattern: /^\d{9}$/, example: '000012345', compact: true },
  JP: { stripeType: 'jp_cn', pattern: /^\d{13}$/, example: '1234567891234', compact: true },
  KR: { stripeType: 'kr_brn', pattern: /^\d{10}$/, example: '1234567890', compact: true },
  MA: { stripeType: 'ma_vat', pattern: /^\d{8}$/, example: '12345678', compact: true },
  NO: { stripeType: 'no_vat', pattern: /^\d{9}MVA$/, example: '123456789MVA', compact: true },
  NZ: { stripeType: 'nz_gst', pattern: /^\d{8,9}$/, example: '123456789', compact: true },
  SA: { stripeType: 'sa_vat', pattern: /^\d{15}$/, example: '123456789012345', compact: true },
  TR: { stripeType: 'tr_tin', pattern: /^\d{10}$/, example: '0123456789', compact: true },
  UA: { stripeType: 'ua_vat', pattern: /^\d{12}$/, example: '123456789012', compact: true },
  US: { stripeType: 'us_ein', pattern: /^\d{9}$/, example: '12-3456789', compact: true },
  ZA: { stripeType: 'za_vat', pattern: /^4\d{9}$/, example: '4123456789', compact: true },
};

export type TaxIdentifierStatus = 'empty' | 'valid' | 'invalid' | 'unsupported-country';

export function getTaxIdentifierFormat(
  country: string | null | undefined,
): TaxIdentifierFormat | undefined {
  if (!country) return undefined;
  return TAX_IDENTIFIER_FORMATS[country.trim().toUpperCase()];
}

export function isTaxIdentifierSupported(country: string | null | undefined): boolean {
  return getTaxIdentifierFormat(country) !== undefined;
}

export function normalizeTaxIdentifier(
  country: string | null | undefined,
  raw: string | null | undefined,
): string {
  const value = (raw ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!value) return '';
  const format = getTaxIdentifierFormat(country);
  if (!format?.compact) return value;
  return value.replace(/[\s.\-/]/g, '');
}

export function checkTaxIdentifier(
  country: string | null | undefined,
  raw: string | null | undefined,
): TaxIdentifierStatus {
  const value = normalizeTaxIdentifier(country, raw);
  if (!value) return 'empty';
  const format = getTaxIdentifierFormat(country);
  if (!format) return 'unsupported-country';
  return format.pattern.test(value) ? 'valid' : 'invalid';
}
